import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const DateWidget = ({
  value,
  onChange,
  label,
  required,
  rawErrors = [],
  id,
  schema,
  formContext,
  options = {},
}) => {
  const isDateOfBirth = options?.isDateOfBirth || false;
  const disablePast = options?.disablePast || false;
  const [selectedDate, setSelectedDate] = useState(null);
  const [textInput, setTextInput] = useState("");
  const [error, setError] = useState("");
  const displayLabel = required ? `${label} *` : label;
  // Handle initial value on mount or value change
  const displayError = rawErrors.length > 0 ? rawErrors[0] : error;

  useEffect(() => {
    if (value) {
      const parsed = dayjs(value, ["YYYY-MM-DD", "MM/DD/YYYY"], true);
      if (parsed.isValid()) {
        setSelectedDate(parsed);
        setTextInput(parsed.format("MM/DD/YYYY"));
        setError("");
      } else {
        setSelectedDate(null);
        setTextInput("");
        setError(
          schema?.errorMessage?.format ||
            "Please enter a valid date in MM/DD/YYYY format"
        );
      }
    }
  }, [value, required, schema]);

  // Validation logic
  const validateDate = (date) => {
    if (!date || !dayjs(date).isValid()) {
      setError(
        schema?.errorMessage?.format ||
          "Please enter a valid date in MM/DD/YYYY format"
      );
      setSelectedDate(null);
      onChange("");
      return;
    }

    const formatted = dayjs(date);
    if (isDateOfBirth && formatted.isAfter(dayjs(), "day")) {
      setError(schema?.errorMessage?.maximum || "Future dates are not allowed");
      setSelectedDate(null);
      onChange("");
      return;
    }
    if (isDateOfBirth && dayjs().diff(formatted, "year") < 18) {
      formContext.setCustomValidation(false);
      setError(
        schema?.errorMessage?.minimumAge ||
          "Client must be at least 18 years old"
      );

      return;
    } else {
      formContext.setCustomValidation(true);
    }

    if (!formatted.isValid() || formatted.year() < 1900) {
      formContext.setCustomValidation(false);
      setError("Invalid or too old date. Please check year.");
      return;
    }
    {
      formContext.setCustomValidation(true);
    }
    if (disablePast && formatted.isBefore(dayjs(), "day")) {
      setError(schema?.errorMessage?.minimum || "Past dates are not allowed");
      setSelectedDate(null);
      onChange("");
      return;
    }

    setError("");
    setSelectedDate(formatted);
    onChange(formatted.format("YYYY-MM-DD"));
  };

  // Calendar date selection
  const handleDateChange = (newDate) => {
    if (dayjs(newDate).isValid()) {
      validateDate(newDate);
    } else {
      if (!displayError) {
        formContext.setCustomValidation(false);
        setError(schema?.errorMessage?.required || "Date of Birth is required");

        return;
      }
    }
  };

  // Manual input typing
  const handleInputChange = (e) => {
    const value = e.target.value;
    setTextInput(value);

    if (value.length === 10) {
      const parsed = dayjs(value, "MM/DD/YYYY", true);
      validateDate(parsed);
    } else {
      setError("");
    }
  };

  // Display error from RJSF or local validation

  return (
    <DatePicker
      label={displayLabel}
      value={selectedDate}
      inputFormat="MM/DD/YYYY"
      disableFuture={isDateOfBirth}
      disablePast={disablePast}
      maxDate={isDateOfBirth ? dayjs().subtract(18, "year") : undefined}
      minDate={disablePast ? dayjs() : undefined}
      onChange={handleDateChange}
      slotProps={{
        textField: {
          label: displayLabel,
          id,
          error: Boolean(error) || Boolean(displayError),
          helperText: error || schema?.description || "",
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          required={required}
          error={Boolean(displayError)}
          helperText={displayError}
          id={id}
          value={textInput}
          onChange={handleInputChange}
        />
      )}
    />
  );
};

export default DateWidget;
